<template>
    <div>
        <v-row class="">
            <v-col cols="12" class="mt-2 py-0">
                <v-checkbox v-model="showTopThree" label="Show top 3" class="ml-3 font-weight-bold" hide-details />
            </v-col>
        </v-row>
        <v-row>
            <v-col v-for="division in teamResults" :key="division.title" xs="12" sm="6" md="6" lg="4" class="py-0">
                <v-list>
                    <v-subheader>
                        {{ division.title }}
                        <v-tooltip top>
                            <template #activator="{ on, attrs }">
                                <v-icon color="primary" dark v-bind="attrs" class="ml-2" v-on="on">
                                    mdi-help-circle-outline
                                </v-icon>
                            </template>
                            <div>
                                <p>
                                    <!-- Levels -->
                                    <span> Levels: {{ division.levels.map(level => level.short).join(', ') }} </span>
                                    <br />

                                    <!-- Genders -->
                                    <span>
                                        Genders: {{ division.genders.map(gender => gender.toUpperCase()).join(', ') }}
                                    </span>
                                    <br />

                                    <!-- Events -->
                                    Events:<br />
                                    <span
                                        v-for="(algorithm, event) in division.events"
                                        :key="`${division.title}-${event}`"
                                        class="pl-4"
                                    >
                                        {{ capitalCase(eventFromProscore(event, 'long')) }}:
                                        <span v-if="algorithm === 'max'">Highest scoring routine counts</span>
                                        <span v-else-if="algorithm === 'total'">Sum of both routines counts</span>
                                        <br />
                                    </span>

                                    <!-- Division -->
                                    <span v-if="division.aggregate > 1"> {{ division.aggregate }} scores count </span>
                                    <span v-else> {{ division.aggregate }} score counts </span>

                                    <!-- Min age -->
                                    <template v-if="division.minAge && division.minAge !== 1">
                                        <br />
                                        <span>Min Age: {{ division.minAge }}</span>
                                    </template>

                                    <!-- Max age -->
                                    <template v-if="division.maxAge && division.maxAge !== 99">
                                        <br />
                                        <span>Max Age: {{ division.maxAge }}</span>
                                    </template>
                                </p>
                            </div>
                        </v-tooltip>
                    </v-subheader>

                    <v-list-group
                        v-for="(teamResult, teamIndex) in divisionResults(division)"
                        :key="`${division.title}-${teamResult.club.id}`"
                        v-model="teamResult.active"
                        no-action
                    >
                        <template #activator>
                            <v-list-item-icon>
                                <scorsync-avatar :person="getClub(teamResult.club.id)" type="club" />
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title> {{ teamIndex + 1 }}. {{ teamResult.club.name }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ numeral(teamResult.totalScore).format('0.000') }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </template>

                        <v-list-group sub-group>
                            <template #activator>
                                <v-list-item-content>
                                    <v-list-item-title>Trampoline Score</v-list-item-title>
                                </v-list-item-content>

                                <v-list-item-icon>
                                    {{ numeral(teamResult.trampolineScore).format('0.000') }}
                                </v-list-item-icon>
                            </template>

                            <v-list-item
                                v-for="(result, index) in teamResult.trampolineScores"
                                :key="`trampoline-${index}`"
                            >
                                <v-list-item-icon>
                                    <scorsync-avatar :person="getAthlete(result.athlete_id)" />
                                </v-list-item-icon>
                                <v-list-item-content>
                                    {{ result.athlete?.name }}
                                </v-list-item-content>
                                <v-list-item-icon>
                                    <span v-if="division.events['TI'] === 'total'">
                                        {{ numeral(result.prelims_score).format('0.000') }}
                                    </span>
                                    <span v-else>
                                        {{ numeral(result.trampoline_scores[0]?.total).format('0.000') }}
                                    </span>
                                </v-list-item-icon>
                            </v-list-item>
                        </v-list-group>

                        <v-list-group sub-group>
                            <template #activator>
                                <v-list-item-content>
                                    <v-list-item-title>Double-Mini Score</v-list-item-title>
                                </v-list-item-content>

                                <v-list-item-icon>
                                    {{ numeral(teamResult.doubleMiniScore).format('0.000') }}
                                </v-list-item-icon>
                            </template>

                            <v-list-item
                                v-for="(result, index) in teamResult.doubleMiniScores"
                                :key="`double-mini-${index}`"
                            >
                                <v-list-item-icon>
                                    <scorsync-avatar :person="getAthlete(result.athlete_id)" />
                                </v-list-item-icon>
                                <v-list-item-content>
                                    {{ result.athlete?.name }}
                                </v-list-item-content>
                                <v-list-item-icon>
                                    {{
                                        numeral(
                                            result.double_mini_scores.reduce(
                                                (partialSum, a) => partialSum + a.total,
                                                0,
                                            ),
                                        ).format('0.000')
                                    }}
                                </v-list-item-icon>
                            </v-list-item>
                        </v-list-group>

                        <v-list-group sub-group>
                            <template #activator>
                                <v-list-item-content>
                                    <v-list-item-title>Tumbling Score</v-list-item-title>
                                </v-list-item-content>

                                <v-list-item-icon>
                                    {{ numeral(teamResult.tumblingScore).format('0.000') }}
                                </v-list-item-icon>
                            </template>

                            <v-list-item
                                v-for="(result, index) in teamResult.tumblingScores"
                                :key="`tumbling-${index}`"
                            >
                                <v-list-item-icon>
                                    <scorsync-avatar :person="getAthlete(result.athlete_id)" />
                                </v-list-item-icon>
                                <v-list-item-content>
                                    {{ result.athlete?.name }}
                                </v-list-item-content>
                                <v-list-item-icon>
                                    {{
                                        numeral(
                                            result.tumbling_scores.reduce((partialSum, a) => partialSum + a.total, 0),
                                        ).format('0.000')
                                    }}
                                </v-list-item-icon>
                            </v-list-item>
                        </v-list-group>
                    </v-list-group>
                </v-list>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import numeral from 'numeral';
import { capitalCase } from 'change-case';
import ScorsyncAvatar from '@/Components/ScorsyncAvatar.vue';
import Club from '@/Models/Club.js';
import Athlete from '@/Models/Athlete.js';
import { eventFromProscore } from '@/helpers.js';

export default {
    name: 'TeamResults',

    components: { ScorsyncAvatar },

    props: {
        teamResults: {
            type: Array,
            required: true,
        },
    },

    data: () => ({
        timer: null,
        showTopThree: true,
    }),

    created() {
        this.timer = window.setInterval(() => {
            this.$emit('refresh');
        }, 60000);
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    methods: {
        capitalCase,
        eventFromProscore,
        numeral,

        divisionResults(division) {
            if (this.showTopThree) {
                return division.results.slice(0, 3);
            }

            return division.results;
        },

        getClub(id) {
            return Club.find(id);
        },

        getAthlete(id) {
            return Athlete.find(id);
        },
    },
};
</script>
